import {
  Menu,
  Box,
  Portal,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useColorModeValue,
  Flex,
  MenuDivider,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { MenuList } from 'src/components/molecules/MenuList';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { TimePeriod } from 'src/types';
import { CustomDateRange } from './CustomDateRange';
import { TimePeriodButton } from './TimePeriodButton';
import { SelectedTimeRange } from './SelectedTimeRange';
import { useFilterContext } from '../../../contexts/FilterContext';
import { Icon } from 'src/components/atoms/Icon';

type TimeRangeSelectorProps = {
  includeTimePeriodButtons?: boolean;
  rightElement?: React.ReactNode;
};

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  includeTimePeriodButtons,
  rightElement,
}) => {
  const { timeRange } = useFilterContext();

  const [showDateRangeSelector, setShowDateRangeSelector] = useState(false);
  const dateRangeSelectorRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const fontActiveColor = useColorModeValue(
    'text.light.active',
    'text.dark.active',
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showDateRangeSelector &&
        popoverContentRef.current &&
        !popoverContentRef.current.contains(event.target as Node) &&
        !dateRangeSelectorRef.current?.contains(event.target as Node)
      ) {
        setShowDateRangeSelector(false);
      }
    };

    if (showDateRangeSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDateRangeSelector]);

  return (
    <Box>
      <Flex
        alignItems="center"
        p="14px"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="border.layout"
        bg="surface.control">
        <Menu>
          <MenuButton
            buttonName="time-range-selector"
            color="text.darkGray"
            fontSize="md"
            fontFamily="marketingPage"
            fontWeight="normal"
            bg="transparent"
            p={0}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}>
            <Flex alignItems="center">
              <Icon iconName="calendar" width={6} height={6} mr={2} />
              <SelectedTimeRange />
            </Flex>
          </MenuButton>
          <MenuList buttonName="time-range-selector">
            <MenuOption
              key="custom-date-range"
              buttonName="time-range-selector"
              onClick={() => {
                setShowDateRangeSelector(true);
              }}
              isSelected={timeRange?.period === TimePeriod.CUSTOM}
              value={TimePeriod.CUSTOM}
              label={TimePeriod.CUSTOM}>
              <Box
                color={
                  timeRange?.period === TimePeriod.CUSTOM ? fontActiveColor : ''
                }>
                Custom Date Range
              </Box>
            </MenuOption>
            <MenuDivider />
            {includeTimePeriodButtons &&
              Object.values(TimePeriod).map((timePeriod) => {
                if (timePeriod === TimePeriod.CUSTOM) {
                  return null;
                }
                return (
                  <MenuOption
                    key={timePeriod}
                    buttonName="time-range-selector"
                    isSelected={timePeriod === timeRange?.period}
                    onClick={() => {}}
                    value={timePeriod}
                    label={timePeriod}>
                    <TimePeriodButton
                      key={timePeriod}
                      timePeriod={timePeriod}
                    />
                  </MenuOption>
                );
              })}
          </MenuList>
        </Menu>
        {rightElement}
      </Flex>
      <Popover
        isOpen={showDateRangeSelector}
        placement="right-start"
        closeOnBlur={false}
        closeOnEsc={true}>
        <PopoverTrigger>
          <Box position="absolute" opacity={0} ref={dateRangeSelectorRef} />
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            w="auto"
            border="none"
            boxShadow="lg"
            ref={popoverContentRef}>
            <CustomDateRange onClose={() => setShowDateRangeSelector(false)} />
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
};
